import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, FileText, ExternalLink, Trash2, RefreshCw } from 'lucide-react';
import { LoadingOverlay } from './';
import { AnalysisService, SessionService, SalesStageService } from '../services';

interface HistoryItem {
  id: string;
  transcriptLength: number;
  salesStage: string;
  processedAt: string;
  analysisId: string;
  title?: string;
}

export const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [sessionId] = useState<string>(() => SessionService.getSessionId());

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const historyData = await AnalysisService.getSessionHistory(sessionId);
      setHistory(historyData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadHistory();
  }, [sessionId, loadHistory]);

  const clearSession = () => {
    if (window.confirm('Czy na pewno chcesz wyczyścić historię sesji? Ta operacja nie może zostać cofnięta.')) {
      SessionService.clearSession();
      window.location.reload();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // State for sales stage labels cache
  const [stageLabels, setStageLabels] = useState<Record<string, string>>({});

  const getSalesStageLabel = (stage: string) => {
    // Return cached label if available
    if (stageLabels[stage]) {
      return stageLabels[stage];
    }
    // Return stage name as fallback while async load happens
    return stage;
  };

  // Load stage labels asynchronously
  useEffect(() => {
    const loadStageLabels = async () => {
      const uniqueStages = [...new Set(history.map(item => item.salesStage))];
      const labels: Record<string, string> = {};
      
      for (const stage of uniqueStages) {
        try {
          const label = await SalesStageService.getSalesStageLabel(stage);
          labels[stage] = label;
        } catch {
          labels[stage] = stage;
        }
      }
      
      setStageLabels(labels);
    };

    if (history.length > 0) {
      loadStageLabels();
    }
  }, [history]);

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold gradient-text mb-2">Historia Analiz</h1>
              <p className="text-gray-600 dark:text-gray-300">
                {history.length} analiz
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={loadHistory}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 
                           text-blue-600 dark:text-blue-400 rounded-xl transition-colors duration-200"
                title="Odśwież historię"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Odśwież</span>
              </button>
              <button
                onClick={clearSession}
                className="flex items-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 
                           text-red-600 dark:text-red-400 rounded-xl transition-colors duration-200"
                title="Wyczyść sesję"
              >
                <Trash2 className="w-5 h-5" />
                <span>Wyczyść sesję</span>
              </button>
              <Link
                to="/"
                className="flex items-center space-x-2 px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                           text-gray-700 dark:text-gray-300 rounded-xl transition-colors duration-200"
              >
                <FileText className="w-5 h-5" />
                <span>Nowa Analiza</span>
              </Link>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {history.length === 0 ? (
            <div className="glass-card rounded-2xl p-8 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                Brak analiz w historii
              </h3>
              <p className="text-gray-500 dark:text-gray-500 mb-6">
                Gdy wykonasz pierwszą analizę, pojawi się tutaj w historii.
              </p>
              <Link
                to="/"
                className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-5 h-5" />
                <span>Rozpocznij pierwszą analizę</span>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="glass-card rounded-2xl p-6 hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                          {item.title || getSalesStageLabel(item.salesStage)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(item.processedAt)}</span>
                        {item.title && (
                          <>
                            <span>•</span>
                            <span className="text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded">
                              {getSalesStageLabel(item.salesStage)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <Link
                        to={`/analysis/${item.analysisId}`}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 
                                   text-blue-600 dark:text-blue-400 rounded-xl transition-colors duration-200"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Zobacz</span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
};